// ============================================================
// CLUBHUB – Pinnwand: Regeln fuer Aushaenge und Aufgaben
// ============================================================
// Rein und browsertauglich. „Heute“ und „jetzt“ kommen immer vom
// Aufrufer (vom Server gereicht), nie aus `new Date()` hier drin —
// sonst rechnen Server und Browser verschieden.
// ============================================================
import type { TaskStatus } from "@/generated/prisma/enums";
import type { AufgabeView, AushangView } from "./views";
import { datumKurz, tageZwischen } from "@/lib/gruppen/datum";

// ── Aushaenge ───────────────────────────────────────────────

export function istAbgelaufen(a: Pick<AushangView, "expiresAt">, jetzt: string): boolean {
  return Boolean(a.expiresAt && a.expiresAt < jetzt);
}

/** Laeuft in den naechsten sieben Tagen ab. */
export function laeuftBaldAb(a: Pick<AushangView, "expiresAt">, jetzt: string): boolean {
  return Boolean(a.expiresAt && a.expiresAt >= jetzt && tageZwischen(jetzt, a.expiresAt) <= 7);
}

/** Absaetze aus einfachem Text: eine Leerzeile trennt, einfache Umbrueche bleiben. */
export function absaetze(text: string): string[] {
  return text
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Ablauf als Ende des gewaehlten Tages. */
export function ablaufAus(datum: string | null): string | null {
  return datum ? `${datum}T23:59:00` : null;
}

// ── Aufgaben ────────────────────────────────────────────────

export const STATUS_FOLGE: TaskStatus[] = ["OPEN", "IN_PROGRESS", "DONE"];

export function istUeberfaellig(t: Pick<AufgabeView, "dueDate" | "status">, heute: string): boolean {
  return Boolean(t.dueDate && t.dueDate < heute && t.status !== "DONE");
}

/** Faellig in den naechsten sieben Tagen (heute eingeschlossen), nicht erledigt. */
export function faelligDieseWoche(t: Pick<AufgabeView, "dueDate" | "status">, heute: string): boolean {
  if (!t.dueDate || t.status === "DONE") return false;
  const d = tageZwischen(heute, t.dueDate);
  return d >= 0 && d <= 7;
}

/** „überfällig seit 3 Tagen“, „heute fällig“, „fällig morgen“, „fällig 12.11.“ */
export function faelligText(t: Pick<AufgabeView, "dueDate" | "status">, heute: string): string | null {
  if (!t.dueDate) return null;
  const d = tageZwischen(heute, t.dueDate);
  if (t.status === "DONE") return `fällig war ${datumKurz(t.dueDate)}`;
  if (d < -1) return `überfällig seit ${-d} Tagen`;
  if (d === -1) return "seit gestern überfällig";
  if (d === 0) return "heute fällig";
  if (d === 1) return "fällig morgen";
  if (d <= 7) return `fällig in ${d} Tagen`;
  const jahr = t.dueDate.slice(0, 4) !== heute.slice(0, 4) ? t.dueDate.slice(0, 4) : "";
  return `fällig ${datumKurz(t.dueDate)}${jahr}`;
}
